/**
 * Resizable Header Component
 *
 * Excel-like column resizing functionality:
 * - Mouse cursor changes on column border hover
 * - Drag to resize column width — pointer events, so mouse, pen AND touch work (TW 36396506)
 * - Double-click the border → column fits its content again («best fit»)
 * - Respects min/max width constraints
 * - Visual feedback during resize
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { ColumnDefinition, TableDataRow } from '../types/table';

interface ResizableHeaderProps<T extends TableDataRow = TableDataRow> {
  column: ColumnDefinition<T>;
  children: React.ReactNode;
  onResize: (columnId: string, width: number) => void;
  /** Dobbeltklikk på kanten: tilpass kolonnen innholdet igjen. */
  onAutoFit?: (columnId: string) => void;
  className?: string;
  onClick?: () => void;
  /** Ekstra attributter på <th> — brukes til dra-og-slipp av kolonner (TW 36396508). */
  thProps?: React.ThHTMLAttributes<HTMLTableCellElement>;
  style?: React.CSSProperties;
}

export const ResizableHeader = <T extends TableDataRow = TableDataRow>({
  column,
  children,
  onResize,
  onAutoFit,
  className = '',
  onClick,
  thProps,
  style: externalStyle
}: ResizableHeaderProps<T>) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const headerRef = useRef<HTMLTableCellElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  // Klikket som følger etter et drag skal ikke sortere kolonnen
  const suppressClickRef = useRef(false);

  /**
   * Handle pointer down on resize handle
   */
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation(); // Prevent column click/sort

    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(column.width);

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [column.width]);

  /**
   * Handle pointer move during resize
   */
  const handleMouseMove = useCallback((e: PointerEvent) => {
    if (!isResizing) return;
    suppressClickRef.current = true;

    const deltaX = e.clientX - startX;
    const newWidth = startWidth + deltaX;

    // Apply constraints
    const constrainedWidth = Math.max(
      column.minWidth,
      column.maxWidth ? Math.min(column.maxWidth, newWidth) : newWidth
    );

    onResize(column.id, constrainedWidth);
  }, [isResizing, startX, startWidth, column.minWidth, column.maxWidth, column.id, onResize]);

  /**
   * Handle mouse up to end resize
   */
  const handleMouseUp = useCallback(() => {
    if (!isResizing) return;

    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    // Nullstill etter at click-eventet fra samme slipp er behandlet
    window.setTimeout(() => { suppressClickRef.current = false; }, 0);
  }, [isResizing]);

  /**
   * Attach/detach global mouse events
   */
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('pointermove', handleMouseMove);
      document.addEventListener('pointerup', handleMouseUp);
      document.addEventListener('pointercancel', handleMouseUp);

      return () => {
        document.removeEventListener('pointermove', handleMouseMove);
        document.removeEventListener('pointerup', handleMouseUp);
        document.removeEventListener('pointercancel', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  /**
   * Handle header click (for sorting)
   */
  const handleHeaderClick = useCallback(() => {
    if (!isResizing && !suppressClickRef.current && onClick) {
      onClick();
    }
  }, [isResizing, onClick]);

  return (
    <th
      {...thProps}
      ref={headerRef}
      data-col-id={column.id}
      className={`${className} relative group`}
      style={{ width: `${column.width}px`, ...externalStyle }}
      onClick={handleHeaderClick}
    >
      {children}

      {/* Resize Handle */}
      {column.resizable && (
        <div
          ref={resizeHandleRef}
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize group-hover:bg-teal-300 transition-colors ${
            isResizing ? 'bg-teal-500' : 'bg-transparent hover:bg-teal-400'
          }`}
          onPointerDown={handlePointerDown}
          onClick={(e) => e.stopPropagation()}
          // Kanten er for bredde — skal aldri starte flytting av kolonnen
          draggable={false}
          onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            onAutoFit?.(column.id);
          }}
          style={{ zIndex: 10, touchAction: 'none' }}
        >
          {/* Wider hit area for easier grabbing (bredere for fingre på nettbrett) */}
          <div className="absolute inset-y-0 -left-2 -right-1"></div>
        </div>
      )}

      {/* Visual feedback during resize */}
      {isResizing && (
        <div className="absolute top-0 right-0 w-px h-full bg-teal-500 shadow-lg"></div>
      )}
    </th>
  );
};